import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link  } from 'react-router-dom';
import { User, Mail, Phone, Lock, Eye, EyeOff } from 'lucide-react';
import { withLang } from '../utils/withLang';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { API_URL, VK_CLIENT_ID, VK_REDIRECT_URI, VK_SCOPE, VK_API_VERSION, VK_AUTHORIZE_URL } from '../config'; // Добавьте в config.js экспорт API_URL
import { generateCodeVerifier, generateCodeChallenge } from '../utils/pkce';

import bgImage from '../assets/bg1.svg';
import logoIcon from '../assets/logo_rus.png';
import { authFetch } from '../utils/authFetch';
import footerDoc1 from '../assets/footer-doc1.docx';
import footerDoc2 from '../assets/footer-doc2.docx';
import tbank from '../assets/Tbank.docx';
import { useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';


export function AuthLayout({ onAuth }) {
  const { t } = useTranslation();
  const { lang = 'ru' } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Registration form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const ref = params.get('ref');

  const path = location.pathname.toLowerCase();
  const [isLogin, setIsLogin] = useState(
    !(path.endsWith('/register') || !!ref)
  );
  
  useEffect(() => {
    if (ref) {
      localStorage.setItem('ref_code', ref);
    }
  }, [ref]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token  = params.get('token');
    const refresh= params.get('refreshToken');
    const name   = params.get('name');
    const email  = params.get('email');

    if (token) {
      // 1) Сохраняем токены
      localStorage.setItem('token', token);
      if (refresh) localStorage.setItem('refreshToken', refresh);

      // 2) Уведомляем родителя, что авторизовались
      onAuth({
        name:  name   ? decodeURIComponent(name)  : undefined,
        email: email  ? decodeURIComponent(email) : undefined
      });

      // ✅ идём с префиксом языка
      if (email === 'admin@mail.com') {
        navigate(`/${lang}/account/moderator`, { replace: true });
      } else {
        navigate(`/${lang}/account`, { replace: true });
      }
    }
  }, [location.search, onAuth, navigate, lang]);
  

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (isLogin) {
      // Вход
      if (!loginEmail || !loginPassword) {
        setError('Введите email и пароль');
        return;
      }
      try {
        const res = await authFetch(`${API_URL}/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: loginEmail, password: loginPassword })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Ошибка входа');

        // Сохраняем токен
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        onAuth(data.user);
        if (data.user.email === 'admin@mail.com') {
          navigate(`/${lang}/account/moderator`, { replace: true });
        }
        else {
          navigate(`/${lang}/account`, { replace: true });
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
    } else {
      // Регистрация
      if (!regName || !regEmail || !regPhone || !regPassword || !regConfirmPassword) {
        setError('Заполните все поля');
        return;
      }
      if (regPassword !== regConfirmPassword) {
        setError('Пароли не совпадают');
        return;
      }
      if (!agreeTerms) {
        setError('Необходимо согласиться с условиями');
        return;
      }
      try {
        const refCode = localStorage.getItem('ref_code') || null;
        const res = await authFetch(`${API_URL}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: regName, 
            email: regEmail, 
            phone: regPhone, 
            password: regPassword, 
            confPassword: regConfirmPassword,
            ref: refCode,          
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Ошибка регистрации');

        // Сохраняем токен
        localStorage.setItem('token', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        onAuth(data.user);
        if (data.user.email === 'admin@mail.com') {
          navigate(`/${lang}/account/moderator`, { replace: true });
        }
        else {
          navigate(`/${lang}/account`, { replace: true });
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
    }
  };

  const providerUrls = {
    google: `${API_URL}/auth/google`,
    yandex: `${API_URL}/auth/yandex`
  };

  const handleSocialAuth = provider => {
    window.location.href = providerUrls[provider];
  };

  async function handleVkAuth() {
  // 1) Генерируем и сохраняем в cookie/session code_verifier
  const verifier = generateCodeVerifier();
  // сохраняем в cookie, чтобы бэкенд мог его прочитать
  document.cookie = `vk_verifier=${verifier};path=/;max-age=300`;

  // 2) Делаем code_challenge
  const challenge = await generateCodeChallenge(verifier);

  // 3) Редиректим на id.vk.com
  const params = new URLSearchParams({
    client_id:             VK_CLIENT_ID,
    redirect_uri:          VK_REDIRECT_URI,
    response_type:         'code',
    scope:                 VK_SCOPE,
    state:                 'vk',
    code_challenge:        challenge,
    code_challenge_method: 'S256'
  });
  params.set('state', JSON.stringify({ lang }));
  window.location.href = `${VK_AUTHORIZE_URL}?${params}`;
}


  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="w-full max-w-sm">
        <Card className="backdrop-blur-sm bg-white/95 border-0 shadow-2xl relative">
          {/* Логотип */}
          <div className="absolute top-4 left-4 z-10">
            <Link to={withLang('/', lang)}>
              <img src={logoIcon} alt="Логотип" className="h-10 w-auto" />
            </Link>
          </div>

          <CardHeader className="text-center pt-16 pb-4 px-6">
            <CardTitle className="text-3xl font-bold gradient-text">
              {isLogin ? `${t('$Enter3866')}` : `${t('$Registration386')}`}
            </CardTitle>
          </CardHeader>

          <CardContent className="px-6 pb-6 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">{t('$NameFamil')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Введите имя и фамилию"
                      value={regName}
                      onChange={e => setRegName(e.target.value)}
                      className="pl-10 h-11 text-base"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('$Email386')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Введите email"
                    value={isLogin ? loginEmail : regEmail}
                    onChange={e =>
                      isLogin ? setLoginEmail(e.target.value) : setRegEmail(e.target.value)
                    }
                    className="pl-10 h-11 text-base"
                    required
                  />
                </div>
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('$Phone386')}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+7 (999) 123-45-67"
                      value={regPhone}
                      onChange={e => setRegPhone(e.target.value)}
                      className="pl-10 h-11 text-base"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">{t('$Passwod386')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('EnterPass')}
                    value={isLogin ? loginPassword : regPassword}
                    onChange={e =>
                      isLogin ? setLoginPassword(e.target.value) : setRegPassword(e.target.value)
                    }
                    className="pl-10 pr-10 h-11 text-base"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-gray-400 p-1"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div className='flex items-center'>
                  <button
                    type="button"
                    className="text-[12px] text-gray"
                    onClick={() => navigate('/forgot-password', { replace: true })}
                  >
                    {t('$Fgt')}
                  </button>
                </div>
              )}

              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('$AcceptPass386')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder={t('$AcceptPass386')}
                      value={regConfirmPassword}
                      onChange={e => setRegConfirmPassword(e.target.value)}
                      className="pl-10 pr-10 h-11 text-base"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-3 text-gray-400 p-1"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}

              {!isLogin && (
                <div className="w-full">
                  <div className="flex items-start gap-3 w-full">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={agreeTerms}
                      onChange={(e) =>
                        setAgreeTerms(e.target.checked)
                      }
                      className="
                        mt-1
                        h-4 w-4
                        min-w-[16px]
                        rounded
                        border-gray-200
                        bg-gray-100
                        text-gray-200
                        focus:ring-gray-300
                        focus:ring-2
                        transition-colors
                        flex-shrink-0
                      "
                      required
                    />
                    <Label
                      htmlFor="terms"
                      className="
                        block
                        w-full
                        text-[12px]
                        text-gray-600
                        leading-relaxed
                        break-words
                      "
                    >
                      {t('$acceptUserAr')}{" "}
                      <a href={footerDoc2} download="Пользовательское соглашение.docx" style={{textDecoration: 'none', color: '#2563eb', fontSize: '12px'}}>
                        <p>
                            {t('$polsogl386')}
                        </p>
                      </a>
                      <a href={footerDoc1} download="Политика конфиденциальности.docx" style={{textDecoration: 'none', color: '#2563eb', fontSize: '12px'}}>
                          <p>
                              {t('$acceptPolicy')}
                          </p>
                      </a>
                      {" "} и {" "}
                      <a href={tbank} download="Агентский договор-оферта.docx" style={{textDecoration: 'none', color: '#2563eb', fontSize: '12px'}}>
                          <p>
                              {t('$Accept386')}
                          </p>
                      </a>
                    </Label>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white"
                disabled={!isLogin && !agreeTerms}
              >
                {isLogin ? `${t('$Enter386')}` : `${t('$Register386')}`}
              </Button>
              {error && (
                <p className="text-center mt-2 text-sm text-gray-700">{error}</p>
              )}
            </form>

            <div className="flex items-center space-x-2">
              <Separator className="flex-1 bg-gray-200 h-px" />
              <span className="text-xs text-gray-500">{t('Orre')}</span>
              <Separator className="flex-1 bg-gray-200 h-px" />
            </div>


            <div className="space-y-3 sm:space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full h-12 sm:h-11 text-sm font-medium"
                onClick={() => handleSocialAuth("google")}
              >
                <div className="w-4 h-4 mr-2">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-full h-full"
                  >
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </div>
                {t('$EnterG387')}
              </Button>

              {/* <Button
                type="button"
                variant="outline"
                className="w-full h-12 sm:h-11 text-sm font-medium"
                onClick={handleVkAuth}
              >
                <div className="w-4 h-4 mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="100" height="100" viewBox="0,0,256,256">
                  <g fill="#5c7cfa" fillRule="nonzero" stroke="none" strokeWidth="1" strokeLinecap="butt" strokeLinejoin="miter" strokeMiterlimit="10" strokeDasharray="" strokeDashoffset="0" fontFamily="none" fontWeight="none" fontSize="none" textAnchor="none" style={{mixBlendMode: 'normal'}}><g transform="scale(5.12,5.12)"><path d="M25,2c-12.682,0 -23,10.318 -23,23c0,12.682 10.318,23 23,23c12.683,0 23,-10.318 23,-23c0,-12.682 -10.317,-23 -23,-23zM34.973,29.535c2.237,1.986 2.702,2.695 2.778,2.816c0.927,1.47 -1.028,1.649 -1.028,1.649h-4.12c0,0 -1.003,0.011 -1.86,-0.557c-1.397,-0.915 -2.86,-2.689 -3.888,-2.381c-0.863,0.258 -0.855,1.424 -0.855,2.421c0,0.357 -0.307,0.517 -1,0.517c-0.693,0 -0.981,0 -1.288,0c-2.257,0 -4.706,-0.76 -7.149,-3.313c-3.456,-3.609 -6.487,-10.879 -6.487,-10.879c0,0 -0.179,-0.366 0.016,-0.589c0.221,-0.25 0.822,-0.218 0.822,-0.218l3.995,-0.001c0,0 0.376,0.071 0.646,0.261c0.223,0.156 0.347,0.454 0.347,0.454c0,0 0.671,2.216 1.526,3.629c1.67,2.758 2.447,2.828 3.014,2.531c0.828,-0.43 0.558,-3.362 0.558,-3.362c0,0 0.037,-1.259 -0.395,-1.82c-0.333,-0.434 -0.97,-0.665 -1.248,-0.701c-0.225,-0.029 0.151,-0.423 0.63,-0.648c0.64,-0.285 1.511,-0.358 3.013,-0.344c1.169,0.011 1.506,0.081 1.962,0.186c1.379,0.318 1.038,1.157 1.038,4.103c0,0.944 -0.13,2.271 0.582,2.711c0.307,0.19 1.359,0.422 3.231,-2.618c0.889,-1.442 1.596,-3.834 1.596,-3.834c0,0 0.146,-0.263 0.373,-0.393c0.232,-0.133 0.225,-0.13 0.543,-0.13c0.318,0 3.507,-0.025 4.207,-0.025c0.699,0 1.355,-0.008 1.468,0.402c0.162,0.589 -0.516,2.607 -2.234,4.797c-2.823,3.594 -3.136,3.258 -0.793,5.336z"></path></g></g>
                  </svg>
                </div>
                Войти через ВКонтакте
              </Button> */}
            </div>

            <div className="text-center pt-4">
              <span className="text-sm text-gray-600">
                {isLogin ? `${t('$NoAccReg')}` : `${t('$AllAccount386')}`}{' '}
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-indigo-600 hover:underline font-medium"
                >
                  {isLogin ? `${t('$Register386')}` : `${t('$Enter386')}`}
                </button>
              </span>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
}
